<template>
  <div v-if="design" class="page-design">
    <DesignView :design="design" />

    <LazyAdAdBar ad-display-location="listing_design_page_banner" />
  </div>
</template>

<script setup lang="ts">
import {Ref} from 'vue';
import {useFetch, useRoute} from '#app';
import {Listing} from '~/@types/listing';
import globalApiHeaders from '~/composables/globalApiHeaders';
import {ApiReturn} from '~/@types/common';

// DATA Definitions
//----------------------------------------------------------------------------------------------------------------------
const route = useRoute();
const config = useRuntimeConfig();
const design: Ref<Listing | null> = ref(null);
const {$analyticsTrack, $metaPixelTrackEvent} = useNuxtApp();

// DATA Fetching
//----------------------------------------------------------------------------------------------------------------------
const {data} = await useFetch(() => `/public/designs/show/${route.params.slug}`,
  { baseURL: config.public.apiUrl, headers: globalApiHeaders()},
);
if (data.value) {
  if ((data.value as ApiReturn).code === 200 && (data.value as ApiReturn).data) {
    // Data is returned
    design.value = (data.value as ApiReturn).data as Listing;
  } else if ((data.value as ApiReturn).code === 404) {
    // No data is returned. Page not found
    throw createError({statusCode: 404, message: 'Design not found', fatal: true});
  } else {
    // Something went wrong
    throw createError({statusCode: 500, message: 'Something went wrong', fatal: true});
  }
}

/**
 * @_HEAD Hook
 */
useHead({
  meta: [
    {
      name: 'canonical',
      content: design?.value?.slug ? `https://www.newhomes.co.nz/design/${design.value.slug}/view` : 'https://www.newhomes.co.nz' + route.fullPath,
    },
  ],
});

const seoMetaData = getSeoMeta();
useSeoMeta({
  title: seoMetaData.title,
  description: seoMetaData.description,
  ogTitle: seoMetaData.title,
  ogDescription: seoMetaData.description,
  ogImage: seoMetaData.imageUrl,
  twitterCard: 'summary_large_image',
  twitterTitle: seoMetaData.title,
  twitterDescription: seoMetaData.description,
  twitterImage: seoMetaData.imageUrl,
});

function getSeoMeta (): {title: string, description: string, robots: string, imageUrl: string} {
  const designName = design.value ? design.value.name : 'Design';
  const seoImageUrl = design.value?.heroImageUrl ?? 'https://store.newhomeshub.co.nz/static-assets/new-homes-og-image-v2.jpg';
  if (design.value && design.value.seo) {
    const { title, description, imageUrl, robots } = design.value.seo;
    return {
      title: title || `${designName} | New Homes - Explore This Home Design in Detail`,
      description: description || `Discover the ${designName} on New Homes. Detailed info about bedrooms, bathrooms, floor area, price, and more. View images, video, and contact details.`,
      robots: robots || 'index, follow',
      imageUrl: imageUrl || seoImageUrl,
    };
  } else {
    return {
      title: `${designName} | New Homes - Explore This Home Design in Detail`,
      description: `Discover the ${designName} on New Homes. Detailed info about bedrooms, bathrooms, floor area, price, and more. View images, video, and contact details.`,
      robots: 'index, follow',
      imageUrl: seoImageUrl,
    };
  }
}

/**
 * @_MOUNTED Hook
 */
onMounted(() => {
  triggerAnalyticsTracking();
});

/**
 * Trigger analytics tracking
 */
function triggerAnalyticsTracking () {
  if (!design.value) return;

  // Track analytics event
  $analyticsTrack({
    action: 'view',
    entity: 'Design',
    property: 'design',
    entitySlug: design.value.slug,
    displayedLocation: 'design page',
    eventData: {
      userIntention: 'active',
      component: 'design page',
    },
  });

  // Track meta event
  $metaPixelTrackEvent('ViewContent', {
    content_name: design.value.name,
    content_category: 'Design',
    content_ids: [design.value.did],
    content_type: 'Listing',
  });
}
</script>

<style lang="scss" scoped>
.page-listing {
  .__feature-wrapper {
    margin-top: 30px;
  }
}
</style>

